import React from 'react';
import { Helmet } from 'react-helmet-async';
import {
  SEOMetadata,
  DEFAULT_SEO,
  generateUrl,
  formatKeywords,
  generateTitle,
  truncateDescription
} from '../utility/seo';

interface SEOHelmetProps extends Partial<SEOMetadata> {
  children?: React.ReactNode;
  structuredData?: string | string[];
}

/**
 * Reusable SEO component that wraps react-helmet-async
 * Provides default values and structured data support
 */
const SEOHelmet: React.FC<SEOHelmetProps> = ({
  title,
  description,
  keywords,
  canonicalUrl,
  ogType = 'website',
  ogImage,
  ogImageAlt,
  twitterCard = 'summary_large_image',
  noindex = false,
  nofollow = false,
  structuredData,
  children
}) => {
  // Generate values with fallbacks to defaults
  const pageTitle = title ? generateTitle(title) : DEFAULT_SEO.defaultTitle;
  const pageDescription = truncateDescription(description || DEFAULT_SEO.defaultDescription);
  const pageKeywords = keywords?.length 
    ? formatKeywords(keywords) 
    : formatKeywords(DEFAULT_SEO.defaultKeywords);
  const pageUrl = canonicalUrl || generateUrl(window.location.pathname);
  const pageImage = ogImage || `${DEFAULT_SEO.baseUrl}${DEFAULT_SEO.defaultOgImage}`;
  const imageAlt = ogImageAlt || `${DEFAULT_SEO.siteName} - ${title || 'Home'}`;

  // Robots meta tag
  const robotsContent = [
    noindex ? 'noindex' : 'index',
    nofollow ? 'nofollow' : 'follow'
  ].join(', ');

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{pageTitle}</title>
      <meta name="title" content={pageTitle} />
      <meta name="description" content={pageDescription} />
      <meta name="keywords" content={pageKeywords} />
      <link rel="canonical" href={pageUrl} />
      <meta name="robots" content={robotsContent} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={pageUrl} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:image" content={pageImage} />
      <meta property="og:image:alt" content={imageAlt} />
      <meta property="og:site_name" content={DEFAULT_SEO.siteName} />

      {/* Twitter */}
      <meta property="twitter:card" content={twitterCard} />
      <meta property="twitter:url" content={pageUrl} />
      <meta property="twitter:title" content={pageTitle} />
      <meta property="twitter:description" content={pageDescription} />
      <meta property="twitter:image" content={pageImage} />
      <meta property="twitter:image:alt" content={imageAlt} />
      <meta name="twitter:site" content={DEFAULT_SEO.twitterHandle} />

      {/* Structured Data (JSON-LD) */}
      {structuredData && (
        Array.isArray(structuredData) ? (
          structuredData.map((schema, index) => (
            <script key={index} type="application/ld+json">
              {schema}
            </script>
          ))
        ) : (
          <script type="application/ld+json">
            {structuredData}
          </script>
        )
      )}

      {/* Additional children (custom meta tags, etc.) */}
      {children}
    </Helmet>
  );
};

export default SEOHelmet;
